import { Eye, ShieldCheck } from 'lucide-react';
import { useProjectMembership } from '../../hooks/useProjectMembership';
import { ACCESS_ROLE_LABELS, ACCESS_ROLE_DESCRIPTIONS } from '../../lib/permissions';

interface RoleAwareBannerProps {
  projectId: string;
}

/**
 * Subtle one-line banner that appears at the top of project pages when
 * the current user's role doesn't include `edit` and `generate` (i.e.
 * Reviewer or Viewer). Owners / Managers / Editors don't see it.
 *
 * The banner exists so a member with limited access immediately
 * understands why generation/upload/edit buttons may be disabled or
 * action attempts may be rejected, without having to click around.
 */
export default function RoleAwareBanner({ projectId }: RoleAwareBannerProps) {
  const { member, role, has, loading } = useProjectMembership(projectId);
  if (loading || !member || !role) return null;
  // Only show for restricted roles.
  if (role === 'owner' || role === 'manager' || role === 'editor') return null;

  const Icon = role === 'reviewer' ? ShieldCheck : Eye;
  const note = role === 'reviewer'
    ? 'You can review and comment, but cannot edit or generate content.'
    : 'You can view and download, but cannot edit, generate, or upload.';
  const tone = role === 'reviewer' ? 'violet' : 'slate';
  const colorMap: Record<string, string> = {
    violet: 'bg-violet-50 ring-violet-200 text-violet-800',
    slate: 'bg-slate-50 ring-slate-200 text-slate-800',
  };
  return (
    <div
      className={`mx-6 my-3 rounded-lg ring-1 ring-inset px-3 py-2 text-sm flex items-center gap-2 ${colorMap[tone]}`}
      title={ACCESS_ROLE_DESCRIPTIONS[role]}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span>
        <strong>{ACCESS_ROLE_LABELS[role]} access.</strong> {note}
      </span>
      {!has('download') && (
        <span className="text-xs opacity-80 ml-2">
          (Downloads also restricted.)
        </span>
      )}
    </div>
  );
}
